import * as React from "react";
import { useState, useEffect, useRef } from "react";
import ProductListItem from "./ProductListItem";
import Pagination from "@Tmp/Pagination";
import { DefaultWidgetProps } from "@TmpApp/types/widgets";
import tmpReact from "@Tmp/Helpers";
import {
    ProductListData,
    ProductListPaginationProps,
    ProductListSearchTagsFunction,
    ProductListSetSearchTagsFunction,
    ProductListTag,
} from "./ProductList.types";
import Toolbar from "./Toolbar";
import Loading from "@Tmp/Loading";
import { getParam, jsonDecode, pushUpdateUrlQueryStringParam } from "@TmpApp/Utils";
import {
    useAxios,
    useDebounce,
    useInViewport,
    useIsDevice,
    useSettings,
    useUpdateEffect,
} from "@Tmp/Hooks";
import clsx from "clsx";
import { LoadingLine, SkeletonLoading } from "..";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Product, TagCategory } from "@TmpApp/types";

const queryClient = new QueryClient();

export default function ProductList(props: DefaultWidgetProps) {
    return (
        <QueryClientProvider client={queryClient}>
            <ProductListInternal {...props} />
        </QueryClientProvider>
    );
}

const ProductListInternal: React.FC<DefaultWidgetProps & { products?: any }> = (props) => {
    if (!props.settings.main_tags) {
        props.settings.main_tags = [];
    }
    if (!props.settings.filter_box_categories) {
        props.settings.filter_box_categories = [];
    }

    const [axiosController, setAxiosController] = useState<AbortController>(null);
    const [lazyLoading, setLazyLoading] = useState(false);
    const [lazyLoadingFinished, setLazyLoadingFinished] = useState(false);
    const tmpSettings = useSettings();

    const resetLazyLoading = () => {
        setLazyLoading(false);
        setLazyLoadingFinished(false);
    };

    const isMobile = useIsDevice("mobile");
    const isTablet = useIsDevice("tablet");
    const isDesktop = useIsDevice("desktop");

    const getPerPage = (): number => {
        if (parseInt(getParam("perpage")) > 0) {
            return parseInt(getParam("perpage"));
        }

        let returnPerPage = parseInt(props.settings?.pagination_per_page.size);
        if (isTablet && props.settings?.pagination_per_page_tablet?.size) {
            returnPerPage = parseInt(props.settings?.pagination_per_page_tablet.size);
        }
        if (isMobile && props.settings?.pagination_per_page_mobile?.size) {
            returnPerPage = parseInt(props.settings?.pagination_per_page_mobile.size);
        }

        if (!returnPerPage) {
            returnPerPage = 12;
        }
        return returnPerPage;
    };

    useUpdateEffect(() => {
        setData((prevData) => ({ ...prevData, perpage: getPerPage() }));
    }, [isMobile, isTablet, isDesktop]);

    const [data, setData] = tmpReact.useState<ProductListData>({
        products: props.products || [],
        tags: [],
        tags_raw: [],
        tag_categories: [],
        page: parseInt(getParam("p-page")) > 0 ? parseInt(getParam("p-page")) : 1,
        perpage: getPerPage(),
        selected_tags: [...jsonDecode(decodeURIComponent(getParam("tags")))],
        search: decodeURI(getParam("q") ? getParam("q") : getParam("s")),
        tags_search: "",
    });

    const { isFetching: loading, isStale } = useQuery<{
        total?: number;
        items: Product[];
        tags?: ProductListTag[];
        tag_categories?: TagCategory[];
    }>({
        refetchOnWindowFocus: false,
        initialData: { items: props.products },
        queryKey: [
            "product-list" + props.id,
            tmpSettings.universe ? tmpSettings.universe : "",
            data.page,
            data.perpage,
            data.selected_tags,
            data.search,
        ],
        queryFn: ({ pageParam: d, queryKey: [key, universe] }) => {
            const controller = new AbortController();

            if (axiosController) {
                axiosController.abort();
            }

            setAxiosController(controller);

            return axios
                .get(
                    "/wp-content/plugins/tmp/api/products.php?" + //@ts-ignore
                        new URLSearchParams({
                            universe: universe,
                            the_dealer: tmpSettings.the_dealer?.id,
                        }).toString(),
                    {
                        signal: controller.signal,
                        params: (() => {
                            const parameters = {
                                q: data.search,
                                "p-page": data.page,
                                perpage: data.perpage,
                                tags: jsonDecode(decodeURIComponent(getParam("tags"))) || [],
                                include_tags: [
                                    ...data?.selected_tags,
                                    ...props.settings?.main_tags,
                                ],
                                tag_category_ids:
                                    props.settings?.filter_boxes?.map?.(
                                        (box) => box?.filter_box_tag_category
                                    ) ?? [],
                                page_id: props.page_id,
                                widget_id: props.id,
                                get_tags: !lazyLoading,
                                product_id: tmpSettings?.current_product?.id,
                            };

                            const brand = getParam("model-brand");
                            if (brand) {
                                parameters["model-brand"] = brand;
                            }

                            const model = getParam("model");
                            if (model) {
                                parameters["model"] = model;
                            }

                            const cubic = getParam("model-cubic");
                            if (cubic) {
                                parameters["model-cubic"] = cubic;
                            }

                            const year = getParam("model-year");
                            if (year) {
                                parameters["model-year"] = year;
                            }

                            return parameters;
                        })(),
                    }
                )
                .then((response) => response.data);
        },
        onError: (err) => {
            setAxiosController(null);
        },
        onSuccess: (response) => {
            setAxiosController(null);
            if (lazyLoading) {
                if (!response.items.length) {
                    setLazyLoadingFinished(true);
                    return;
                }

                setData((prevData) => ({
                    ...prevData,
                    products: [...prevData.products, ...response.items],
                    total_products: response.total,
                }));
                return;
            }

            const tags = parseTags(response.tags);
            setData((prev) => ({
                ...prev,
                products: response.items,
                total_products: response.total,
                tags: returnSearchTags({ tags_search: data.tags_search, tags_raw: tags }),
                tags_raw: tags,
                tag_categories: response.tag_categories,
            }));
        },
    });

    useEffect(() => {
        window.addEventListener("popstate", (e) => {
            setData((prev) => ({
                ...prev,
                page: parseInt(getParam("p-page")) > 0 ? parseInt(getParam("p-page")) : data.page,
                perpage:
                    parseInt(getParam("perpage")) > 0
                        ? parseInt(getParam("perpage"))
                        : data.perpage,
                selected_tags: getParam("tags")
                    ? jsonDecode(decodeURIComponent(getParam("tags")))
                    : [],
                search: getParam("q") ? decodeURI(getParam("q")) : data.search,
            }));
        });
    }, []);

    const parseTags = (tags: null | undefined | ProductListTag[]) => {
        return (tags ?? []).filter((tag) =>
            props.settings?.main_tags.includes(tag.name) ? false : true
        );
    };

    const returnSearchTags: ProductListSearchTagsFunction = (params) => {
        const value = params.tags_search.toLowerCase().replace(/\s+/g, "");
        return params.tags_raw.filter(
            (tag) => tag.name.toLowerCase().replace(/\s+/g, "").indexOf(value) > -1
        );
    };

    const searchTags: ProductListSetSearchTagsFunction = (params): void => {
        setData((prev) => jQuery.extend({}, prev, { tags: returnSearchTags(params) }));
    };

    return (
        <>
            <Toolbar
                settings={props.settings}
                tags={data.tags}
                tags_raw={data.tags_raw}
                tag_categories={data.tag_categories}
                search={data.search}
                tags_search={data.tags_search}
                selected_tags={data.selected_tags}
                setData={setData}
                searchTags={searchTags}
                loading={loading}
                resetLazyLoading={resetLazyLoading}
            />
            {loading && data.products?.length > 0 && <LoadingLine />}
            <div className={clsx("tmp-product-list", props.settings?.product_strict_width)}>
                {loading && data.products?.length <= 0
                    ? Array.from({ length: data.perpage }, (x, i) => i).map((value, index) => (
                          <ItemSkeleton key={index} />
                      ))
                    : data.products.map((product, index) => (
                          <ProductListItem
                              key={product.id}
                              settings={props.settings}
                              product={product}
                          />
                      ))}
            </div>
            {Math.ceil(data.total_products / data.perpage) > 1 && (
                <ProductListPagination
                    settings={props.settings}
                    loading={loading}
                    data={data}
                    setData={setData}
                    lazyLoading={lazyLoading}
                    setLazyLoading={setLazyLoading}
                    lazyLoadingFinished={lazyLoadingFinished}
                    setLazyLoadingFinished={setLazyLoadingFinished}
                />
            )}
        </>
    );
};

const ProductListPagination: React.FC<ProductListPaginationProps> = (props) => {
    const lazyLoadRef = useRef<HTMLDivElement>();
    const inViewport = useInViewport(lazyLoadRef);
    const isLazyLoadingSupported = "IntersectionObserver" in window;

    useDebounce(
        () => {
            if (!props.lazyLoading || !inViewport) {
                return;
            }

            lazyLoad();
        },
        200,
        [inViewport, props.data.products]
    );

    const lazyLoad = (): void => {
        props.setData((prev) => ({ ...prev, page: prev.page + 1 }));
    };

    const onPageChange = (page): void => {
        pushUpdateUrlQueryStringParam("p-page", page);
        props.setData((prev) => ({ ...prev, page: page }));
    };

    return (
        <div ref={lazyLoadRef} className="tmp-product-list__pagination__container">
            {props.lazyLoading ? null : (
                <Pagination
                    loading={props.loading}
                    page={props.data.page}
                    perpage={props.data.perpage}
                    total={props.data.total_products}
                    onPageChange={onPageChange}
                />
            )}
            {props.data.page === 1 && !props.lazyLoading && isLazyLoadingSupported && (
                <div className="tmp-product-list__pagination__show-all-container">
                    <span
                        className="tmp-product-list__pagination__show-all"
                        onClick={() => {
                            props.setLazyLoading(true);
                            if (inViewport) {
                                lazyLoad();
                            }
                        }}
                    >
                        {props.settings?.pagination_see_all_text}
                    </span>
                </div>
            )}
            {props.loading && props.lazyLoading && <Loading />}
        </div>
    );
};

const ItemSkeleton: React.FC = () => {
    return (
        <div className="tmp-product-list__item">
            <SkeletonLoading
                className="tmp-product-list__item__image-container"
                style={{ marginBottom: "10px" }}
            />
            <SkeletonLoading height="15px" style={{ marginBottom: "10px" }} />
            <SkeletonLoading width="75%" height="15px" />
        </div>
    );
};
